import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { JobArchitectureMetadata } from '~/shared/data-contract/job-architecture';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const createQueryFn =
    (fetchJobArchitectureMetadata: ApplicationFetchers['fetchJobArchitectureMetadata']) =>
    (jobArchitectureId: string): (() => Promise<JobArchitectureMetadata | null>) => {
        return async () => {
            const result = await fetchJobArchitectureMetadata(jobArchitectureId);

            if (result.isOk === true) {
                return result.value;
            }

            throw result.error;
        };
    };

export const useJobArchitectureMetadata = (
    jobArchitectureId: string | undefined,
): UseQueryResult<JobArchitectureMetadata | null, Error> => {
    const { fetchJobArchitectureMetadata } = useApplicationFetchers();

    return useQuery({
        enabled: !is.undefined(jobArchitectureId),
        queryFn: createQueryFn(fetchJobArchitectureMetadata)(jobArchitectureId!),
        queryKey: jobArchitectureKeys.metadata(jobArchitectureId!),
        staleTime: 10_000,
        throwOnError: false,
    });
};
